import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Trash } from '@phosphor-icons/react';
import { api } from '../../lib/api';
import { SkeletonAdminDetail } from '../../components/Skeleton';
import { useConfirm } from '../../components/ConfirmModal';

export default function AdminUserDetail() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [userType, setUserType] = useState('user');

  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const [deleting, setDeleting] = useState(false);

  const [showTempPassword, setShowTempPassword] = useState(false);
  const [tempPassword, setTempPassword] = useState('');
  const [savingPassword, setSavingPassword] = useState(false);
  const [tempPasswordSet, setTempPasswordSet] = useState(false);

  const [userClinics, setUserClinics] = useState([]);
  const [allClinics, setAllClinics] = useState([]);
  const [addClinicId, setAddClinicId] = useState('');
  const [clinicsBusy, setClinicsBusy] = useState(false);
  const [clinicsError, setClinicsError] = useState('');
  const { confirm, confirmModal } = useConfirm();

  useEffect(() => {
    api.admin.users()
      .then((users) => {
        const found = users.find((u) => u.id === userId);
        if (found) {
          setUser(found);
          setFirstName(found.first_name || '');
          setLastName(found.last_name || '');
          setEmail(found.email || '');
          setUserType(found.user_type || 'user');
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false));

    Promise.all([api.admin.userClinics(userId), api.admin.clinics()])
      .then(([mine, all]) => {
        setUserClinics(mine);
        setAllClinics(all);
      })
      .catch((err) => setClinicsError(err.message));
  }, [userId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      await api.admin.updateUser(userId, {
        first_name: firstName,
        last_name: lastName,
        email,
        user_type: userType,
      });
      navigate('/admin/users');
    } catch (err) {
      setError(err.message);
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    if (!user) return;
    const name = `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email;
    const ok = await confirm({
      title: `Delete ${name}?`,
      message: 'This permanently removes their account, profile, and clinic memberships. This cannot be undone.',
      confirmLabel: 'Delete user',
      destructive: true,
    });
    if (!ok) return;
    setDeleting(true);
    setError('');
    try {
      await api.admin.deleteUser(userId);
      navigate('/admin/users');
    } catch (err) {
      setError(err.message);
      setDeleting(false);
    }
  };

  const handleSetTempPassword = async () => {
    if (!tempPassword || tempPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    setSavingPassword(true);
    setError('');
    setTempPasswordSet(false);
    try {
      await api.admin.setUserPassword(userId, tempPassword);
      setTempPassword('');
      setShowTempPassword(false);
      setTempPasswordSet(true);
    } catch (err) {
      setError(err.message);
    }
    setSavingPassword(false);
  };

  const handleAddClinic = async () => {
    if (!addClinicId) return;
    setClinicsBusy(true);
    setClinicsError('');
    try {
      await api.admin.addClinicMember(addClinicId, userId);
      const mine = await api.admin.userClinics(userId);
      setUserClinics(mine);
      setAddClinicId('');
    } catch (err) {
      setClinicsError(err.message);
    }
    setClinicsBusy(false);
  };

  const handleRemoveClinic = async (clinicId, clinicName) => {
    const ok = await confirm({
      title: `Remove from ${clinicName}?`,
      message: 'This user will lose access to that clinic. They can be added back later.',
      confirmLabel: 'Remove',
      destructive: true,
    });
    if (!ok) return;
    setClinicsBusy(true);
    setClinicsError('');
    try {
      await api.admin.removeClinicMember(clinicId, userId);
      setUserClinics((prev) => prev.filter((c) => c.id !== clinicId));
    } catch (err) {
      setClinicsError(err.message);
    }
    setClinicsBusy(false);
  };

  if (loading) return <SkeletonAdminDetail />;
  if (!user) return <p className="text-sm text-gray-500">User not found.</p>;

  return (
    <div>
      <button
        onClick={() => navigate('/admin/users')}
        className="flex items-center gap-1.5 text-sm text-primary font-medium mb-4 hover:underline"
      >
        <ArrowLeft size={16} /> Back
      </button>

      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 mb-6">
        <h1 className="text-2xl md:text-[32px] font-semibold text-text">
          {user.first_name} {user.last_name}
        </h1>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={() => { setShowTempPassword(!showTempPassword); setTempPasswordSet(false); }}
            className="px-4 py-2 text-sm font-medium text-primary border border-primary-30 rounded-lg hover:bg-[#F5E9FD] transition-colors"
          >
            Set Temp Password
          </button>
          <button
            onClick={handleDelete}
            disabled={deleting}
            className="px-3 py-2 text-sm font-medium text-destructive border border-destructive-30 rounded-lg hover:bg-destructive-10 transition-colors flex items-center gap-1.5 disabled:opacity-50"
          >
            <Trash size={16} />
            {deleting ? 'Deleting...' : 'Delete'}
          </button>
        </div>
      </div>

      {showTempPassword && (
        <div className="bg-white rounded-xl border border-[#E6E6E6] p-4 md:p-6 mb-6">
          <p className="text-sm font-bold text-text mb-3">Temp Password</p>
          <div className="flex flex-col sm:flex-row gap-3">
            <input
              type="text"
              value={tempPassword}
              onChange={(e) => setTempPassword(e.target.value)}
              placeholder="Min 6 characters"
              className="flex-1 border border-[#E6E6E6] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-30"
            />
            <button
              onClick={handleSetTempPassword}
              disabled={savingPassword}
              className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary-80 disabled:opacity-50 flex-shrink-0"
            >
              {savingPassword ? 'Saving...' : 'Set'}
            </button>
          </div>
        </div>
      )}

      {tempPasswordSet && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">
          Temporary password set.
        </div>
      )}

      <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-[#E6E6E6] p-4 md:p-6">
        <div className="mb-4">
          <label className="block text-sm font-medium text-text mb-1">First Name</label>
          <input
            type="text"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            required
            className="w-full border border-[#E6E6E6] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-30"
          />
        </div>
        <div className="mb-4">
          <label className="block text-sm font-medium text-text mb-1">Last Name</label>
          <input
            type="text"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            required
            className="w-full border border-[#E6E6E6] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-30"
          />
        </div>
        <div className="mb-4">
          <label className="block text-sm font-medium text-text mb-1">Email</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="w-full border border-[#E6E6E6] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-30"
          />
        </div>
        <div className="mb-4">
          <label className="block text-sm font-medium text-text mb-1">Type</label>
          <select
            value={userType}
            onChange={(e) => setUserType(e.target.value)}
            className="w-full border border-[#E6E6E6] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-30 bg-white"
          >
            <option value="user">User</option>
            <option value="admin">Clinic Admin</option>
            <option value="super_admin">Super Admin</option>
          </select>
        </div>

        {error && (
          <div className="mb-3 p-2 bg-destructive-10 border border-destructive-30 rounded-lg text-sm text-destructive">
            {error}
          </div>
        )}

        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => navigate('/admin/users')}
            className="px-4 py-2 text-sm font-medium text-primary border border-primary-30 rounded-lg hover:bg-[#F5E9FD]"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary-80 disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>

      <div className="bg-white rounded-xl border border-[#E6E6E6] p-4 md:p-6 mt-6">
        <h2 className="text-lg font-bold text-text mb-1">Clinics</h2>
        <p className="text-sm text-gray-600 mb-4">Clinics this user belongs to.</p>

        {userClinics.length === 0 ? (
          <p className="text-sm text-gray-500 mb-4">Not a member of any clinic yet.</p>
        ) : (
          <ul className="divide-y divide-[#E6E6E6] border border-[#E6E6E6] rounded-lg mb-4">
            {userClinics.map((c) => (
              <li key={c.id} className="flex items-center justify-between px-4 py-3">
                <button
                  type="button"
                  onClick={() => navigate(`/admin/clinics/${c.id}`)}
                  className="text-sm text-text hover:text-primary hover:underline text-left"
                >
                  {c.name}
                </button>
                <button
                  type="button"
                  onClick={() => handleRemoveClinic(c.id, c.name)}
                  disabled={clinicsBusy}
                  className="text-destructive hover:bg-destructive-10 p-1.5 rounded disabled:opacity-50"
                  aria-label={`Remove from ${c.name}`}
                >
                  <Trash size={16} />
                </button>
              </li>
            ))}
          </ul>
        )}

        <div className="flex flex-col sm:flex-row gap-3">
          <select
            value={addClinicId}
            onChange={(e) => setAddClinicId(e.target.value)}
            disabled={clinicsBusy}
            className="flex-1 border border-[#E6E6E6] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-30 bg-white disabled:opacity-50"
          >
            <option value="">Add to clinic…</option>
            {allClinics
              .filter((c) => !userClinics.some((uc) => uc.id === c.id))
              .map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
          </select>
          <button
            type="button"
            onClick={handleAddClinic}
            disabled={!addClinicId || clinicsBusy}
            className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary-80 disabled:opacity-50 flex-shrink-0"
          >
            {clinicsBusy ? 'Working…' : 'Add'}
          </button>
        </div>

        {clinicsError && (
          <div className="mt-3 p-2 bg-destructive-10 border border-destructive-30 rounded-lg text-sm text-destructive">
            {clinicsError}
          </div>
        )}
      </div>
      {confirmModal}
    </div>
  );
}
